import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import employeeId from 'modules/payroll_bundle/components/common/helper/employeeId'
import fullName from 'modules/payroll_bundle/components/common/helper/fullName'
import sanitizeEmployeeMatchedData from 'modules/payroll_bundle/components/common/helper/sanitizeEmployeeMatchedData'
import type { SupplementMatchDataType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import { DataImports } from 'modules/payroll_bundle/store/collections/dataImports'

const useMatchEmployeeData = (
  matchedData: SupplementMatchDataType[],
  filteredEmployees: Employee[]
) => {
  const [matches, setMatches] = React.useState<SupplementMatchDataType[]>([])
  const dataImportsCollection: DataImports = useLazyRef(() => new DataImports([])).current

  useAsyncData(async () => {
    const sanitized = sanitizeEmployeeMatchedData(matchedData)

    if (filteredEmployees.length) {
      // POST /payroll/data_imports/match_employees_data
      // { first_name: "Jonathan", last_name: "Small", 1669627265755: 12 }
      // =>
      // { first_name: "Jonathan", last_name: "Small", 1669627265755: 12,
      //   employee_id: null, userId: null, IBAN: null }  // no match in this case
      const data = await dataImportsCollection.rpc('match_employees_data', {
        data: sanitized,
      })
      matchedData.forEach((match, index) => {
        data[index].id = index + 1

        data[index].full_name = fullName(match?.name, match?.last_name)
        const employee = users.get(data[index].userId)?.employee

        const hasEmployeeId = employeeId(data[index].userId, filteredEmployees)

        if (hasEmployeeId) {
          const legalEntity = employee && employeeLegalEntity(employee)
          data[index].legal_entity_id = legalEntity?.id
          data[index].legal_entity_name = legalEntity?.name
        } else {
          // When the imported supplement does not match any employee for the period
          // we need set the userId and employee_id to null
          data[index].userId = null
          data[index].employee_id = null
        }
      })

      setMatches(data)
    }
  }, [filteredEmployees.length])

  return matches
}

export default useMatchEmployeeData
